import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Mention, MentionsInput } from "react-mentions";
import "./MultiEntityMentionsEditor.css";

// Entity type configuration
export const ENTITY_CONFIGS = {
  okr: {
    icon: "🎯",
    color: "#3B82F6",
    label: "OKRs",
    searchPlaceholder: "Search OKRs...",
    trigger: "@okr",
  },
  segment: {
    icon: "📊",
    color: "#10B981",
    label: "Segments",
    searchPlaceholder: "Search Segments...",
    trigger: "@segment",
  },
  proposition: {
    icon: "💡",
    color: "#8B5CF6",
    label: "Propositions",
    searchPlaceholder: "Search Propositions...",
    trigger: "@proposition",
  },
  company: {
    icon: "🏢",
    color: "#F97316",
    label: "Companies",
    searchPlaceholder: "Search Companies...",
    trigger: "@company",
  },
  user: {
    icon: "👤",
    color: "#6B7280",
    label: "Users",
    searchPlaceholder: "Search Users...",
    trigger: "@user",
  },
};

export interface EntitySuggestion {
  entity_type: "okr" | "segment" | "proposition" | "company" | "user";
  id: string;
  name: string;
  description?: string;
  metadata?: Record<string, any>;
}

interface MultiEntityMentionsEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onSearchEntities?: (
    entityType: string,
    query: string,
  ) => Promise<EntitySuggestion[]>;
  disabled?: boolean;
  entityConfigOverrides?: {
    [K in keyof typeof ENTITY_CONFIGS]?: Partial<typeof ENTITY_CONFIGS[K]>;
  };
}

export interface MultiEntityMentionsEditorRef {
  getTokenizedContent: () => string;
}

const MultiEntityMentionsEditor = forwardRef<
  MultiEntityMentionsEditorRef,
  MultiEntityMentionsEditorProps
>(
  (
    { value, onChange, placeholder, onSearchEntities, disabled = false, entityConfigOverrides },
    ref,
  ) => {
    // Merge entity config overrides with defaults
    const entityConfigs = {
      ...ENTITY_CONFIGS,
      ...Object.fromEntries(
        Object.entries(entityConfigOverrides || {}).map(([key, override]) => [
          key,
          { ...ENTITY_CONFIGS[key as keyof typeof ENTITY_CONFIGS], ...(override || {}) },
        ])
      ),
    } as typeof ENTITY_CONFIGS;
    
    const [displayValue, setDisplayValue] = useState("");
    const [entityMap, setEntityMap] = useState<Map<string, EntitySuggestion>>(
      new Map(),
    );
    const [suggestions, setSuggestions] = useState<
      Record<string, EntitySuggestion[]>
    >({
      okr: [],
      segment: [],
      proposition: [],
      company: [],
      user: [],
    });

    // Parse existing tokens on mount and value change
    useEffect(() => {
      // Token pattern for all entity types - 3 parts: entity_type:uuid:display_name
      const tokenPattern =
        /\{\{(okr|segment|proposition|company|user):([a-f0-9-]+):([^}]+)\}\}/g;
      let parsedValue = value;
      const newEntityMap = new Map<string, EntitySuggestion>();

      let match;
      while ((match = tokenPattern.exec(value)) !== null) {
        const [fullMatch, entityType, id, displayName] = match;
        // Get the icon for this entity type
        const config =
          ENTITY_CONFIGS[entityType as keyof typeof ENTITY_CONFIGS];
        // Convert token to mention format for react-mentions with icon
        const mentionFormat = `@[${config.icon} ${displayName}](${entityType}:${id})`;
        parsedValue = parsedValue.replace(fullMatch, mentionFormat);
        newEntityMap.set(`${entityType}:${id}`, {
          entity_type: entityType as
            | "okr"
            | "segment"
            | "proposition"
            | "company"
            | "user",
          id,
          name: displayName,
        });
      }

      setDisplayValue(parsedValue);
      setEntityMap(newEntityMap);
    }, [value]);

    const handleChange = (
      event: { target: { value: string } },
      newValue: string,
    ) => {
      setDisplayValue(newValue);

      // Convert to tokenized format before calling onChange
      let tokenizedText = newValue;
      const mentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g;

      tokenizedText = tokenizedText.replace(
        mentionPattern,
        (match, display, fullId) => {
          const [entityType, id] = fullId.split(":");
          const entity =
            entityMap.get(fullId) ||
            suggestions[entityType]?.find((s) => s.id === id);

          // Remove icon from display name if present
          const cleanDisplay = display.replace(/^(?:🎯|📊|💡|🏢|👤)\s*/u, "");
          const name = entity?.name || cleanDisplay;

          return `{{${entityType}:${id}:${name}}}`;
        },
      );

      onChange(tokenizedText);
    };

    // Search function for a specific entity type
    const searchEntities = useCallback(
      async (
        entityType: string,
        query: string,
        callback: (data: Array<{ id: string; display: string }>) => void,
      ) => {
        if (!onSearchEntities) {
          callback([]);
          return;
        }

        try {
          const results = await onSearchEntities(entityType, query);

          // Update suggestions for this entity type
          setSuggestions((prev) => ({
            ...prev,
            [entityType]: results,
          }));

          // If no results, show a helpful message
          if (results.length === 0) {
            const config = entityConfigs[entityType as keyof typeof entityConfigs];
            const emptyMessage = query
              ? `No ${config.label.toLowerCase()} found matching "${query}"`
              : `No ${config.label.toLowerCase()} available`;

            callback([
              {
                id: `empty-${entityType}`,
                display: `💡 ${emptyMessage}`,
              },
            ]);
            return;
          }

          // Format for react-mentions
          const formatted = results.map((entity) => ({
            id: `${entity.entity_type}:${entity.id}`,
            display: `${entityConfigs[entity.entity_type].icon} ${entity.name}`,
          }));

          callback(formatted);
        } catch (error) {
           
          console.error(`Error searching ${entityType}:`, error);
          const config = entityConfigs[entityType as keyof typeof entityConfigs];
          callback([
            {
              id: `error-${entityType}`,
              display: `⚠️ Error searching ${config.label.toLowerCase()}`,
            },
          ]);
        }
      },
      [onSearchEntities, entityConfigs],
    );

    const handleAddMention = (id: string | number, display: string) => {
      const idStr = String(id);

      // Don't add empty state or error messages as actual mentions
      if (idStr.startsWith("empty-") || idStr.startsWith("error-") || idStr.startsWith("help-")) {
        return;
      }

      const [entityType, entityId] = idStr.split(":");

      // Find the entity in suggestions
      const entity = suggestions[entityType]?.find((s) => s.id === entityId);

      // Create entity object even if not found in suggestions (use display name)
      const entityObj = entity || {
        entity_type: entityType as
          | "okr"
          | "segment"
          | "proposition"
          | "company"
          | "user",
        id: entityId,
        name: display.replace(/^(?:🎯|📊|💡|🏢|👤|⚠️)\s*/u, ""), // Remove icon from display
      };

      // Update entity map
      const newEntityMap = new Map(entityMap);
      newEntityMap.set(idStr, entityObj);
      setEntityMap(newEntityMap);
    };

    // Method to get tokenized content
    const getTokenizedContent = () => {
      let tokenizedText = displayValue;

      // Convert mentions to our token format
      // react-mentions format: @[display](entityType:id)
      const mentionPattern = /@\[([^\]]+)\]\(([^)]+)\)/g;

      tokenizedText = tokenizedText.replace(
        mentionPattern,
        (match, display, fullId) => {
          const [entityType, id] = fullId.split(":");
          const entity =
            entityMap.get(fullId) ||
            suggestions[entityType]?.find((s) => s.id === id);

          // Remove icon from display name if present
          const cleanDisplay = display.replace(/^(?:🎯|📊|💡|🏢|👤)\s*/u, "");
          const name = entity?.name || cleanDisplay;

          return `{{${entityType}:${id}:${name}}}`;
        },
      );

      return tokenizedText;
    };

    useImperativeHandle(ref, () => ({
      getTokenizedContent,
    }));

    return (
      <MentionsInput
        value={displayValue}
        onChange={handleChange}
        placeholder={
          placeholder ||
          "Type @okr, @segment, @proposition, @company, or @user to link entities..."
        }
        className="mentions-input narrative-editor multi-entity-editor"
        disabled={disabled}
      >
        {/* OKR Mentions */}
        <Mention
          trigger="@okr"
          markup="@[__display__](__id__)"
          data={(query, callback) => searchEntities("okr", query, callback)}
          renderSuggestion={(
            suggestion: { id: string | number; display?: string },
            search: string,
            highlightedDisplay: React.ReactNode,
          ) => (
            <div
              className="mention-suggestion"
              style={{ borderLeft: `3px solid ${entityConfigs.okr.color}` }}
            >
              {highlightedDisplay}
            </div>
          )}
          onAdd={handleAddMention}
          displayTransform={(id: string | number, display: string) => display}
        />

        {/* Segment Mentions */}
        <Mention
          trigger="@segment"
          markup="@[__display__](__id__)"
          data={(query, callback) => searchEntities("segment", query, callback)}
          renderSuggestion={(
            suggestion: { id: string | number; display?: string },
            search: string,
            highlightedDisplay: React.ReactNode,
          ) => (
            <div
              className="mention-suggestion"
              style={{
                borderLeft: `3px solid ${entityConfigs.segment.color}`,
              }}
            >
              {highlightedDisplay}
            </div>
          )}
          onAdd={handleAddMention}
          displayTransform={(id: string | number, display: string) => display}
        />

        {/* Proposition Mentions */}
        <Mention
          trigger="@proposition"
          markup="@[__display__](__id__)"
          data={(query, callback) =>
            searchEntities("proposition", query, callback)
          }
          renderSuggestion={(
            suggestion: { id: string | number; display?: string },
            search: string,
            highlightedDisplay: React.ReactNode,
          ) => (
            <div
              className="mention-suggestion"
              style={{
                borderLeft: `3px solid ${entityConfigs.proposition.color}`,
              }}
            >
              {highlightedDisplay}
            </div>
          )}
          onAdd={handleAddMention}
          displayTransform={(id: string | number, display: string) => display}
        />

        {/* Company Mentions */}
        <Mention
          trigger="@company"
          markup="@[__display__](__id__)"
          data={(query, callback) => searchEntities("company", query, callback)}
          renderSuggestion={(
            suggestion: { id: string | number; display?: string },
            search: string,
            highlightedDisplay: React.ReactNode,
          ) => (
            <div
              className="mention-suggestion"
              style={{
                borderLeft: `3px solid ${entityConfigs.company.color}`,
              }}
            >
              {highlightedDisplay}
            </div>
          )}
          onAdd={handleAddMention}
          displayTransform={(id: string | number, display: string) => display}
        />

        {/* User Mentions */}
        <Mention
          trigger="@user"
          markup="@[__display__](__id__)"
          data={(query, callback) => searchEntities("user", query, callback)}
          renderSuggestion={(
            suggestion: { id: string | number; display?: string },
            search: string,
            highlightedDisplay: React.ReactNode,
          ) => (
            <div
              className="mention-suggestion"
              style={{
                borderLeft: `3px solid ${entityConfigs.user.color}`,
              }}
            >
              {highlightedDisplay}
            </div>
          )}
          onAdd={handleAddMention}
          displayTransform={(id: string | number, display: string) => display}
        />
      </MentionsInput>
    );
  },
);

MultiEntityMentionsEditor.displayName = "MultiEntityMentionsEditor";

export default MultiEntityMentionsEditor;
